import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect } from 'react';
import {
    fetchUserFeatures,
    clearFeatures,
    selectHasSubscription,
    selectPlanType,
    selectPlanName,
    selectEndDate,
    selectIsLegacy,
    selectIsUnlimited,
    selectFeatures,
    selectFeaturesLoading,
    selectFeaturesLoaded,
} from '../features/featuresSlice';
import { selectUserId, selectUserEmail } from '../components/Login/loginSlice';

/**
 * Хук для работы с функциями подписки пользователя
 * 
 * Использование:
 * const { canUseStorage, canUseVoiceAssistant, canUseChat, planName } = useFeatures();
 * 
 * if (!canUseStorage) {
 *   return <UpgradePrompt feature="storage" />;
 * }
 */
export const useFeatures = () => {
    const dispatch = useDispatch();

    // Данные из Redux
    const userId = useSelector(selectUserId);
    const userEmail = useSelector(selectUserEmail);
    const hasSubscription = useSelector(selectHasSubscription);
    const planType = useSelector(selectPlanType);
    const planName = useSelector(selectPlanName);
    const endDate = useSelector(selectEndDate);
    const isLegacy = useSelector(selectIsLegacy);
    const isUnlimited = useSelector(selectIsUnlimited);
    const features = useSelector(selectFeatures);
    const loading = useSelector(selectFeaturesLoading);
    const loaded = useSelector(selectFeaturesLoaded);

    // Загрузить features при первом рендере (если есть userId или email)
    const loadFeatures = useCallback(() => {
        if ((userId || userEmail) && !loaded && !loading) {
            dispatch(fetchUserFeatures({ userId, email: userEmail }));
        }
    }, [dispatch, userId, userEmail, loaded, loading]);

    // Принудительная перезагрузка features
    const reloadFeatures = useCallback(() => {
        if (userId || userEmail) {
            dispatch(fetchUserFeatures({ userId, email: userEmail }));
        }
    }, [dispatch, userId, userEmail]);

    // Очистка при логауте
    const resetFeatures = useCallback(() => {
        dispatch(clearFeatures());
    }, [dispatch]);

    // Проверка доступности конкретного типа чата
    const canUseChat = useCallback((chatType) => {
        // Если features ещё не загружены - разрешаем доступ (оптимистичный подход)
        if (!loaded) return true;
        if (!hasSubscription || !features?.chatTypes) return false;
        return features.chatTypes.includes(chatType);
    }, [hasSubscription, features, loaded]);

    // Проверка доступности голосового помощника
    const canUseVoiceAssistant = !loaded ? false : (hasSubscription && features?.voiceAssistant === true);

    // Проверка доступности хранилища файлов
    const canUseStorage = !loaded ? false : (hasSubscription && features?.storage === true);

    // Максимальное количество устройств
    const maxDevices = features?.maxDevices || 0;

    // Проверка истечения подписки
    const isExpired = useCallback(() => {
        if (isUnlimited) return false;
        if (!endDate) return true;
        return new Date(endDate) < new Date();
    }, [endDate, isUnlimited]);

    // Дней до окончания подписки
    const daysUntilExpiry = useCallback(() => {
        if (isUnlimited) return Infinity;
        if (!endDate) return 0;
        const diff = new Date(endDate) - new Date();
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    }, [endDate, isUnlimited]);

    // Форматированная дата окончания
    const formattedEndDate = endDate
        ? new Date(endDate).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        })
        : null;

    // Получить название плана на русском
    const getPlanDisplayName = useCallback(() => {
        if (!hasSubscription) return 'Нет подписки';
        if (isUnlimited) return 'Безлимитный';
        return planName || 'Подписка активна';
    }, [hasSubscription, isUnlimited, planName]);

    // Получить список доступных типов чата
    const availableChatTypes = features?.chatTypes || [];

    return {
        // Состояние загрузки
        loading,
        loaded,

        // Основные данные подписки
        hasSubscription,
        planType,
        planName,
        endDate,
        formattedEndDate,
        isLegacy,
        isUnlimited,

        // Проверки доступности функций
        canUseVoiceAssistant,
        canUseStorage,
        canUseChat,
        maxDevices,
        availableChatTypes,

        // Утилиты
        isExpired,
        daysUntilExpiry,
        getPlanDisplayName,

        // Методы
        loadFeatures,
        reloadFeatures,
        resetFeatures,
    };
};

export default useFeatures;
